import type {CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {InputWrapper} from './TextInputWrapper';

const TextAreaInput = styled(
  'textarea',
  (
    {
      $autoGrow,
      $resize = 'vertical',
      rows,
    }: {
      $autoGrow?: boolean;
      $resize?: 'both' | 'horizontal' | 'vertical' | 'none';
      rows?: number;
    },
    designColors,
  ) => ({
    appearance: 'none',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 4,
    // The auto-grow measurement sets the height to scrollHeight, which
    // includes padding; border-box keeps that from compounding.
    boxSizing: $autoGrow ? 'border-box' : undefined,
    color: designColors.contentText,
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    height: '100%',
    lineHeight: '20px',
    minHeight: rows ? undefined : 120,
    overflowY: $autoGrow ? 'hidden' : undefined,
    padding: '8px 10px',
    resize: $autoGrow ? 'none' : $resize,
    width: '100%',

    ':focus': {
      outline: 'none',
    },

    '::placeholder': {color: '#c6caca'},
    '::-webkit-placeholder': {color: '#c6caca'},
  }),
  {className: 'TextArea'},
);

type Props = {
  autoGrow?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  maxLength?: number;
  minLength?: number;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLTextAreaElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLTextAreaElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLTextAreaElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLTextAreaElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLTextAreaElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLTextAreaElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLTextAreaElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  onRef?: React.Ref<HTMLTextAreaElement | null>;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  resize?: 'both' | 'horizontal' | 'vertical' | 'none';
  rows?: number;
  style?: CSSProperties;
  tabIndex?: number;
  value: string;
};

const TextArea = (
  {
    autoGrow,
    disabled,
    invalid,
    nativeEvents,
    onChange,
    resize,
    style,
    value,
    ...rest
  }: Props,
  ref: React.Ref<HTMLTextAreaElement>,
) => {
  const ownRef = React.useRef<HTMLTextAreaElement | null>(null);
  const handleRef = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      ownRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current =
          el;
      }
    },
    [ref],
  );

  const resizeToFit = React.useCallback(() => {
    const el = ownRef.current;
    if (!el) {
      return;
    }
    // Collapse first so the textarea can shrink when content is removed.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useLayoutEffect(() => {
    if (autoGrow) {
      resizeToFit();
    }
  }, [autoGrow, resizeToFit, value]);

  React.useEffect(() => {
    if (!autoGrow) {
      return;
    }
    // Width changes re-wrap the text, which changes the required height.
    window.addEventListener('resize', resizeToFit);
    return () => {
      window.removeEventListener('resize', resizeToFit);
    };
  }, [autoGrow, resizeToFit]);

  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      e.preventDefault();

      onChange?.((e.target as HTMLTextAreaElement).value);
      nativeOnChange?.(e);
    },
    [onChange, nativeOnChange],
  );
  return (
    <InputWrapper
      $disabled={!!disabled}
      $invalid={!!invalid}
      style={{height: 'auto', ...style}}
    >
      <TextAreaInput
        $autoGrow={autoGrow}
        $resize={resize}
        disabled={disabled}
        {...rest}
        dir="auto"
        {...nativeEvents}
        onChange={handleChange}
        ref={handleRef}
        value={value}
      />
    </InputWrapper>
  );
};

export default React.forwardRef<HTMLTextAreaElement, Props>(TextArea);
